<template>
  <div class="faq-page">

    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Course FAQs</h1>
        <p>Manage frequently asked questions for courses.</p>
      </div>

      <button class="primary-btn" @click="openAddModal">
        <i class="bx bx-plus"></i>
        Add FAQ
      </button>
    </div>

    <!-- Filters -->
    <div class="filter-card">

      <div class="search-box">
        <i class="bx bx-search"></i>
        <input
          v-model="search"
          type="text"
          placeholder="Search FAQs..."
        />
      </div>

      <select v-model="selectedCourse">
        <option value="">All Courses</option>

        <option
          v-for="course in courses"
          :key="course.course_id"
          :value="course.course_id"
        >
          {{ course.title }}
        </option>
      </select>

      <!-- <select v-model="selectedCategory">
        <option value="">All Categories</option>

        <option
          v-for="category in categories"
          :key="category.cat_id"
          :value="category.cat_id"
        >
          {{ category.cat_name }}
        </option>
      </select> -->

      <button class="reset-btn" @click="resetFilters">
        <i class="bx bx-reset"></i>
        Reset
      </button>

    </div>

    <!-- Table -->
    <div class="table-card">

      <div class="table-top">
        <div>
          <h3>FAQ List</h3>
          <span>{{ totalFaqs }} FAQs</span>
        </div>
      </div>

      <div class="table-wrapper">
        <table>

          <thead>
            <tr>
              <th>Question</th>
              <th>Course</th>
              <!-- <th>Category</th> -->
              <th>Parent Type</th>
              <th>Description</th>
              <th>Created</th>
              <th class="action-col">Actions</th>
            </tr>
          </thead>

          <tbody>

            <!-- Loading -->
            <tr v-if="loading">
              <td colspan="7">
                <div class="empty-state">
                  <i class="bx bx-loader-alt bx-spin"></i>
                  <h4>Loading FAQs...</h4>
                  <p>Please wait while FAQs are being loaded.</p>
                </div>
              </td>
            </tr>

            <!-- FAQ Rows -->
            <tr
              v-else
              v-for="faq in faqs"
              :key="faq.faq_id"
            >

              <!-- Question -->
              <td>
                <div class="faq-info">

                  <div class="faq-icon">
                    <i class="bx bx-help-circle"></i>
                  </div>

                  <div>
                    <strong>
                      {{ faq.faq_title }}
                    </strong>

                    <small>
                      #{{ faq.faq_id }}
                    </small>
                  </div>

                </div>
              </td>

              <!-- Course -->
              <td>
                <span class="course-name">
                  {{ getCourseName(faq.faq_parent) }}
                </span>
              </td>

              <!-- Category -->
              <!-- <td>
                <span class="category-badge">
                  {{ getCategoryName(faq.faq_cat) }}
                </span>
              </td> -->

              <!-- Parent Type -->
              <td>
                <span class="type-badge">
                  {{ faq.faq_parent_type || '-' }}
                </span>
              </td>

              <!-- Description -->
              <td>
                <div class="description-cell">
                  {{ truncateText(faq.faq_desc, 70) }}
                </div>
              </td>

              <!-- Created -->
              <td>
                {{ formatDate(faq.faq_datetime) }}
              </td>

              <!-- Actions -->
              <td>
                <div class="actions">

                  <button
                    class="icon-btn view"
                    title="View"
                    @click="viewFaq(faq)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="editFaq(faq)"
                  >
                    <i class="bx bx-edit"></i>
                  </button>

                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="confirmDelete(faq)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>

                </div>
              </td>

            </tr>

            <!-- Empty -->
            <tr v-if="!loading && faqs.length === 0">
              <td colspan="7">

                <div class="empty-state">
                  <i class="bx bx-help-circle"></i>

                  <h4>No FAQs found</h4>

                  <p>
                    Try changing your search or filters.
                  </p>
                </div>

              </td>
            </tr>

          </tbody>

        </table>
      </div>

      <!-- Pagination -->
      <div
        v-if="!loading && totalFaqs > 0"
        class="pagination"
      >

        <span>
          Showing
          {{ startIndex + 1 }}
          -
          {{ endIndex }}
          of
          {{ totalFaqs }}
        </span>

        <div class="pagination-buttons">

          <!-- Previous -->
          <button
            :disabled="currentPage === 1"
            @click="previousPage"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <!-- Page Numbers -->
          <button
            v-for="pageNumber in totalPagesCount"
            :key="pageNumber"
            :class="{ active: currentPage === pageNumber }"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber }}
          </button>

          <!-- Next -->
          <button
            :disabled="currentPage === totalPagesCount"
            @click="nextPage"
          >
            <i class="bx bx-chevron-right"></i>
          </button>

        </div>

      </div>

    </div>

    <!-- Add / Edit Modal -->
    <div
      v-if="showFormModal"
      class="modal-overlay"
      @click.self="closeFormModal"
    >

      <div class="modal">

        <div class="modal-header">

          <div>
            <h3>
              {{ isEditing ? 'Edit FAQ' : 'Add FAQ' }}
            </h3>

            <p>
              {{
                isEditing
                  ? 'Update course FAQ information.'
                  : 'Create a new course FAQ.'
              }}
            </p>
          </div>

          <button
            class="close-btn"
            @click="closeFormModal"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <div class="modal-body">

          <div class="form-grid">

            <!-- Course -->
            <div class="form-group">
              <label>Course <span>*</span></label>

              <select v-model="form.faq_parent">
                <option value="">Select Course</option>

                <option
                  v-for="course in courses"
                  :key="course.course_id"
                  :value="course.course_id"
                >
                  {{ course.title }}
                </option>
              </select>
            </div>

            <!-- Category -->
            <!-- <div class="form-group">
              <label>Category</label>

              <select v-model="form.faq_cat">
                <option value="">Select Category</option>

                <option
                  v-for="category in categories"
                  :key="category.cat_id"
                  :value="category.cat_id"
                >
                  {{ category.cat_name }}
                </option>
              </select>
            </div> -->

            <!-- Parent Type -->
            <div class="form-group">
              <label>Parent Type</label>

              <input
                v-model="form.faq_parent_type"
                type="text"
                placeholder="e.g. course"
              />
            </div>

            <!-- Question -->
            <div class="form-group full">
              <label>Question <span>*</span></label>

              <textarea
                v-model="form.faq_title"
                rows="3"
                placeholder="Enter frequently asked question"
              ></textarea>
            </div>

            <!-- Meta Title -->
            <div class="form-group">
              <label>Meta Title</label>

              <input
                v-model="form.faq_meta_title"
                type="text"
                placeholder="Enter SEO meta title"
              />
            </div>

            <!-- Keywords -->
            <div class="form-group">
              <label>Meta Keywords</label>

              <input
                v-model="form.faq_meta_keyword"
                type="text"
                placeholder="faq, course, learning"
              />
            </div>

            <!-- Answer -->
            <div class="form-group full">
              <label>Answer / Description</label>

              <textarea
                v-model="form.faq_desc"
                rows="7"
                placeholder="Enter FAQ answer"
              ></textarea>
            </div>

          </div>

        </div>

        <div class="modal-footer">

          <button
            class="secondary-btn"
            :disabled="saving"
            @click="closeFormModal"
          >
            Cancel
          </button>

          <button
            class="primary-btn"
            :disabled="saving"
            @click="saveFaq"
          >
            <i
              class="bx"
              :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"
            ></i>

            {{
              saving
                ? (isEditing ? 'Updating...' : 'Creating...')
                : (isEditing ? 'Update FAQ' : 'Create FAQ')
            }}
          </button>

        </div>

      </div>

    </div>

    <!-- View Modal -->
    <div
      v-if="showViewModal"
      class="modal-overlay"
      @click.self="closeViewModal"
    >

      <div class="modal view-modal">

        <div class="modal-header">

          <div>
            <h3>FAQ Details</h3>
            <p>View course FAQ information.</p>
          </div>

          <button
            class="close-btn"
            @click="closeViewModal"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <div
          v-if="selectedFaq"
          class="modal-body"
        >

          <div class="faq-preview">

            <div class="big-faq-icon">
              <i class="bx bx-help-circle"></i>
            </div>

            <div>
              <h2>
                {{ selectedFaq.faq_title }}
              </h2>

              <p>
                {{ getCourseName(selectedFaq.faq_parent) }}

                <!-- <span>•</span>

                {{ getCategoryName(selectedFaq.faq_cat) }} -->
              </p>
            </div>

          </div>

          <div class="detail-grid">

            <div class="detail-item">
              <label>FAQ ID</label>

              <strong>
                #{{ selectedFaq.faq_id }}
              </strong>
            </div>

            <div class="detail-item">
              <label>Course</label>

              <strong>
                {{ getCourseName(selectedFaq.faq_parent) }}
              </strong>
            </div>

            <!-- <div class="detail-item">
              <label>Category</label>

              <strong>
                {{ getCategoryName(selectedFaq.faq_cat) }}
              </strong>
            </div> -->

            <div class="detail-item">
              <label>Parent Type</label>

              <strong>
                {{ selectedFaq.faq_parent_type || '-' }}
              </strong>
            </div>

            <div class="detail-item">
              <label>Meta Title</label>

              <strong>
                {{ selectedFaq.faq_meta_title || '-' }}
              </strong>
            </div>

            <div class="detail-item">
              <label>Created</label>

              <strong>
                {{ formatDate(selectedFaq.faq_datetime) }}
              </strong>
            </div>

          </div>

          <!-- Question -->
          <div class="content-section">

            <label>Question</label>

            <div class="content-box question-box">
              {{ selectedFaq.faq_title }}
            </div>

          </div>

          <!-- Answer -->
          <div class="content-section">

            <label>Answer</label>

            <div class="content-box answer-box">
              {{
                selectedFaq.faq_desc ||
                'No answer available.'
              }}
            </div>

          </div>

          <!-- Keywords -->
          <div class="content-section">

            <label>Meta Keywords</label>

            <div class="keyword-box">
              {{
                selectedFaq.faq_meta_keyword ||
                '-'
              }}
            </div>

          </div>

        </div>

        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="closeViewModal"
          >
            Close
          </button>

          <button
            class="primary-btn"
            @click="editFaq(selectedFaq)"
          >
            <i class="bx bx-edit"></i>
            Edit FAQ
          </button>

        </div>

      </div>

    </div>

    <!-- Delete Modal -->
    <div
      v-if="showDeleteModal"
      class="modal-overlay"
      @click.self="closeDeleteModal"
    >

      <div class="delete-modal">

        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h3>Delete FAQ?</h3>

        <p>
          Are you sure you want to delete
          <strong>
            {{ selectedFaq?.faq_title }}
          </strong>?
          This action cannot be undone.
        </p>

        <div class="delete-actions">

          <button
            class="secondary-btn"
            :disabled="deleting"
            @click="closeDeleteModal"
          >
            Cancel
          </button>

          <button
            class="danger-btn"
            :disabled="deleting"
            @click="deleteFaq"
          >
            <i
              class="bx"
              :class="deleting ? 'bx-loader-alt bx-spin' : 'bx-trash'"
            ></i>

            {{ deleting ? 'Deleting...' : 'Delete FAQ' }}
          </button>

        </div>

      </div>

    </div>

  </div>
</template>
<script>
import {
  getFaqsApi,
  getFaqApi,
  createFaqApi,
  updateFaqApi,
  deleteFaqApi,
} from "@/apis/faqapis";

import { getCoursesApi } from "@/apis/courseapis";
import { getCategoriesApi } from "@/apis/categoryapis";

export default {
  name: "CourseFaqsScreen",

  data() {
    return {
      search: "",
      selectedCourse: "",
      selectedCategory: "",

      currentPage: 1,
      perPage: 10,

      totalFaqs: 0,
      totalPagesCount: 1,

      faqs: [],
      courses: [],
      categories: [],

      loading: false,
      saving: false,
      deleting: false,

      showFormModal: false,
      showViewModal: false,
      showDeleteModal: false,

      isEditing: false,
      selectedFaq: null,

      form: {
        faq_parent: "",
        faq_parent_type: "Course",
        faq_title: "",
        faq_meta_title: "",
        faq_meta_keyword: "",
        faq_desc: "",
        faq_cat: "",
      },
    };
  },

  computed: {
    filteredFaqs() {
      return this.faqs;
    },

    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },

    endIndex() {
      return this.startIndex + this.faqs.length;
    },
  },

  watch: {
    search() {
      this.currentPage = 1;
      this.loadFaqs();
    },

    selectedCourse() {
      this.currentPage = 1;
      this.loadFaqs();
    },

    selectedCategory() {
      this.currentPage = 1;
      this.loadFaqs();
    },
  },

  mounted() {
    this.selectedCourse = String(this.$route.query.course_id || "");
    this.loadFaqs();
    this.loadCourses();
    this.loadCategories();
  },

  methods: {
    async loadFaqs() {
      this.loading = true;

      try {
        const res = await getFaqsApi({
          page: this.currentPage,
          limit: this.perPage,
          search: this.search,
          course_id: this.selectedCourse,
          category_id: this.selectedCategory,
        });

        if (res && res.status !== false) {
          this.faqs = res.faqs || [];
          this.totalFaqs = Number(res.total || 0);

          this.totalPagesCount = Math.max(
            Number(res.totalPages || 1),
            1
          );

          if (Number(res.page)) {
            this.currentPage = Number(res.page);
          }
        } else {
          this.faqs = [];
          this.totalFaqs = 0;
          this.totalPagesCount = 1;
        }
      } catch (error) {
        console.error("Load FAQs Error:", error);
        this.faqs = [];
        this.totalFaqs = 0;
        this.totalPagesCount = 1;
      } finally {
        this.loading = false;
      }
    },

    async loadCourses() {
      try {
        const res = await getCoursesApi({
          page: 1,
          limit: 100,
          search: "",
        });

        if (res && res.status !== false) {
          this.courses = res.courses || [];
        } else {
          this.courses = [];
        }
      } catch (error) {
        console.error("Load Courses Error:", error);
        this.courses = [];
      }
    },

    async loadCategories() {
      try {
        const res = await getCategoriesApi({
          page: 1,
          limit: 100,
          search: "",
        });

        if (res && res.status !== false) {
          this.categories = res.categories || [];
        } else {
          this.categories = [];
        }
      } catch (error) {
        console.error("Load Categories Error:", error);
        this.categories = [];
      }
    },

    getCourseName(courseId) {
      const course = this.courses.find(
        (item) =>
          Number(item.course_id) === Number(courseId)
      );

      return course ? course.title : "-";
    },

    getCategoryName(categoryId) {
      const category = this.categories.find(
        (item) =>
          Number(item.cat_id) === Number(categoryId)
      );

      return category ? category.cat_name : "-";
    },

    truncateText(text, length) {
      if (!text) return "-";

      if (text.length <= length) {
        return text;
      }

      return text.substring(0, length) + "...";
    },

    formatDate(date) {
      if (!date) return "-";

      const value = new Date(date);

      if (Number.isNaN(value.getTime())) {
        return date;
      }

      return value.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    resetFilters() {
      this.search = "";
      this.selectedCourse = "";
      this.selectedCategory = "";
      this.currentPage = 1;

      this.loadFaqs();
    },

    resetForm() {
      this.form = {
        faq_parent: this.selectedCourse || "",
        faq_parent_type: "Course",
        faq_title: "",
        faq_meta_title: "",
        faq_meta_keyword: "",
        faq_desc: "",
        faq_cat: "",
      };
    },

    openAddModal() {
      this.isEditing = false;
      this.selectedFaq = null;

      this.resetForm();

      this.showFormModal = true;
    },

    async editFaq(faq) {
      if (!faq) return;

      this.isEditing = true;

      this.showViewModal = false;

      /*
       * Fetch latest FAQ data before editing.
       */
      try {
        const res = await getFaqApi(faq.faq_id);

        const data =
          res && res.faq
            ? res.faq
            : faq;

        this.selectedFaq = data;

        this.form = {
          faq_parent: data.faq_parent || "",
          faq_parent_type:
            data.faq_parent_type || "Course",
          faq_title: data.faq_title || "",
          faq_meta_title:
            data.faq_meta_title || "",
          faq_meta_keyword:
            data.faq_meta_keyword || "",
          faq_desc: data.faq_desc || "",
          faq_cat: data.faq_cat || "",
        };

        this.showFormModal = true;
      } catch (error) {
        console.error("Edit FAQ Error:", error);

        this.selectedFaq = faq;

        this.form = {
          faq_parent: faq.faq_parent || "",
          faq_parent_type:
            faq.faq_parent_type || "Course",
          faq_title: faq.faq_title || "",
          faq_meta_title:
            faq.faq_meta_title || "",
          faq_meta_keyword:
            faq.faq_meta_keyword || "",
          faq_desc: faq.faq_desc || "",
          faq_cat: faq.faq_cat || "",
        };

        this.showFormModal = true;
      }
    },

    closeFormModal() {
      if (this.saving) return;

      this.showFormModal = false;
      this.resetForm();
    },

    async saveFaq() {
      if (!this.form.faq_title.trim()) {
        alert("Please enter FAQ question.");
        return;
      }

      if (!this.form.faq_parent) {
        alert("Please select a course.");
        return;
      }

      this.saving = true;

      try {
        let res;

        if (this.isEditing) {
          if (!this.selectedFaq?.faq_id) {
            alert("FAQ ID is missing.");
            return;
          }

          res = await updateFaqApi({
            faq_id: this.selectedFaq.faq_id,
            ...this.form,
          });
        } else {
          res = await createFaqApi(this.form);
        }

        if (res && res.status !== false) {
          alert(
            res.success ||
              (this.isEditing
                ? "FAQ updated successfully."
                : "FAQ created successfully.")
          );

          this.showFormModal = false;
          this.resetForm();

          this.currentPage = 1;

          await this.loadFaqs();
        } else {
          alert(
            res?.success ||
              (this.isEditing
                ? "FAQ update failed."
                : "FAQ creation failed.")
          );
        }
      } catch (error) {
        console.error("Save FAQ Error:", error);

        alert(
          error?.response?.data?.success ||
            "Something went wrong while saving FAQ."
        );
      } finally {
        this.saving = false;
      }
    },

    async viewFaq(faq) {
      if (!faq) return;

      try {
        const res = await getFaqApi(faq.faq_id);

        if (res && res.status !== false && res.faq) {
          this.selectedFaq = res.faq;
        } else {
          this.selectedFaq = faq;
        }
      } catch (error) {
        console.error("View FAQ Error:", error);

        this.selectedFaq = faq;
      }

      this.showViewModal = true;
    },

    confirmDelete(faq) {
      this.selectedFaq = faq;
      this.showDeleteModal = true;
    },

    async deleteFaq() {
      if (!this.selectedFaq?.faq_id) {
        return;
      }

      this.deleting = true;

      try {
        const res = await deleteFaqApi(
          this.selectedFaq.faq_id
        );

        if (res && res.status !== false) {
          alert(
            res.success || "FAQ deleted successfully."
          );

          this.showDeleteModal = false;
          this.selectedFaq = null;

          /*
           * If last item on the current page was deleted,
           * move to previous page when required.
           */
          if (
            this.faqs.length === 1 &&
            this.currentPage > 1
          ) {
            this.currentPage--;
          }

          await this.loadFaqs();
        } else {
          alert(
            res?.success || "FAQ deletion failed."
          );
        }
      } catch (error) {
        console.error("Delete FAQ Error:", error);

        alert(
          error?.response?.data?.success ||
            "Something went wrong while deleting FAQ."
        );
      } finally {
        this.deleting = false;
      }
    },

    goToPage(page) {
      if (
        page < 1 ||
        page > this.totalPagesCount ||
        page === this.currentPage
      ) {
        return;
      }

      this.currentPage = page;
      this.loadFaqs();
    },

    previousPage() {
      if (this.currentPage <= 1) {
        return;
      }

      this.currentPage--;
      this.loadFaqs();
    },

    nextPage() {
      if (
        this.currentPage >= this.totalPagesCount
      ) {
        return;
      }

      this.currentPage++;
      this.loadFaqs();
    },

    closeViewModal() {
      this.showViewModal = false;
      this.selectedFaq = null;
    },

    closeDeleteModal() {
      if (this.deleting) return;

      this.showDeleteModal = false;
      this.selectedFaq = null;
    },
  },
};
</script>
<style scoped>
.faq-page {
  min-height: 100%;
  background: #f8fafc;
  padding: 24px;
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.page-header h1 {
  margin: 0 0 5px;
  font-size: 24px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.danger-btn,
.reset-btn {
  border: 0;
  border-radius: 9px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s;
}

.primary-btn {
  background: #2563eb;
  color: #fff;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn {
  background: #f1f5f9;
  color: #334155;
}

.secondary-btn:hover {
  background: #e2e8f0;
}

.danger-btn {
  background: #dc2626;
  color: #fff;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Filter */
.filter-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-card select {
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-size: 14px;
  outline: none;
}

.search-box input {
  width: 100%;
  padding: 0 12px 0 40px;
}

.filter-card select {
  min-width: 190px;
  padding: 0 35px 0 12px;
}

.search-box input:focus,
.filter-card select:focus,
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #2563eb;
}

.reset-btn {
  height: 42px;
  background: #f1f5f9;
  color: #475569;
}

/* Table */
.table-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.table-top {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.table-top h3 {
  margin: 0 0 4px;
  font-size: 16px;
}

.table-top span {
  color: #64748b;
  font-size: 13px;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
}

th {
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 13px 16px;
  text-align: left;
  white-space: nowrap;
}

td {
  padding: 15px 16px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
}

tr:hover td {
  background: #fafcff;
}

.action-col {
  text-align: right;
}

.faq-info {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 280px;
}

.faq-icon {
  width: 40px;
  height: 40px;
  border-radius: 9px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.faq-info strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  max-width: 270px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.faq-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.course-name {
  color: #334155;
  font-weight: 500;
}

.category-badge,
.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 9px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
}

.description-cell {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #64748b;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
}

.icon-btn.view {
  color: #475569;
}

.icon-btn.edit {
  color: #2563eb;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn:hover {
  background: #f8fafc;
}

/* Empty */
.empty-state {
  padding: 55px 20px;
  text-align: center;
}

.empty-state i {
  font-size: 42px;
  color: #cbd5e1;
}

.empty-state h4 {
  margin: 10px 0 5px;
  color: #334155;
}

.empty-state p {
  margin: 0;
  color: #94a3b8;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 13px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 34px;
  height: 34px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  border-radius: 7px;
  cursor: pointer;
}

.pagination-buttons button.active {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.pagination-buttons button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 760px;
  max-width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.modal-header h3 {
  margin: 0 0 4px;
  font-size: 18px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 7px;
  background: #f1f5f9;
  color: #475569;
  font-size: 20px;
  cursor: pointer;
}

.modal-body {
  padding: 22px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 17px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.form-group label span {
  color: #dc2626;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 11px 12px;
  color: #334155;
  font-size: 14px;
  outline: none;
  background: #fff;
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.modal-footer {
  padding: 16px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* View */
.faq-preview {
  display: flex;
  align-items: center;
  gap: 15px;
  padding-bottom: 22px;
  border-bottom: 1px solid #e2e8f0;
}

.big-faq-icon {
  width: 58px;
  height: 58px;
  border-radius: 12px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  flex-shrink: 0;
}

.faq-preview h2 {
  margin: 0 0 5px;
  font-size: 19px;
  line-height: 1.4;
}

.faq-preview p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.faq-preview p span {
  margin: 0 6px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-top: 22px;
}

.detail-item {
  padding: 14px;
  background: #f8fafc;
  border-radius: 9px;
}

.detail-item label {
  display: block;
  color: #64748b;
  font-size: 11px;
  margin-bottom: 5px;
}

.detail-item strong {
  color: #0f172a;
  font-size: 13px;
  line-height: 1.5;
}

.content-section {
  margin-top: 22px;
}

.content-section > label {
  display: block;
  margin-bottom: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.content-box {
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  padding: 14px;
  color: #475569;
  font-size: 14px;
  line-height: 1.7;
  white-space: pre-wrap;
}

.question-box {
  color: #0f172a;
  font-weight: 500;
}

.answer-box {
  min-height: 80px;
}

.keyword-box {
  padding: 11px 13px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 13px;
  word-break: break-word;
}

/* Delete */
.delete-modal {
  width: 420px;
  max-width: 100%;
  background: #fff;
  border-radius: 14px;
  padding: 30px;
  text-align: center;
}

.delete-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 25px;
}

.delete-modal h3 {
  margin: 0 0 8px;
  font-size: 18px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
}

.delete-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 24px;
}

/* Responsive */
@media (max-width: 900px) {
  .filter-card {
    flex-wrap: wrap;
  }

  .search-box {
    flex-basis: 100%;
  }

  .filter-card select {
    flex: 1;
  }
}

@media (max-width: 650px) {
  .faq-page {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .page-header .primary-btn {
    padding: 9px 12px;
  }

  .page-header .primary-btn i {
    display: none;
  }

  .filter-card {
    display: block;
  }

  .search-box {
    margin-bottom: 10px;
  }

  .filter-card select {
    width: 100%;
    margin-bottom: 10px;
  }

  .reset-btn {
    width: 100%;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full {
    grid-column: auto;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>